/**
 * IndexedDB — connexion + schéma (Phase 2, incrément 2.1).
 *
 * Cf. ADR 0006 pour le contexte. Wrapper unique autour de la DB
 * `nestor`. Trois object stores :
 *
 *   - `blobs` : photos + voice messages, clé `key` (string).
 *     Valeur `BlobEntry { key, blob, contentType, size, addedAt }`.
 *   - `ai-memory` : 1 entrée par version de la mémoire AI, clé `id`.
 *     Reste petit (réservé Phase 2.4).
 *   - `mutations` : queue offline-first, clé auto-increment `seq`.
 *     Vide en Phase 2 (rempli Phase 5).
 *
 * Une seule fonction publique : `openDb()` retourne une `Promise<
 * IDBPDatabase<NestorSchema>>`. Mise en cache : un seul handle DB
 * réutilisé pour toute la session (l'API `idb` autorise plusieurs
 * `openDB` mais c'est gâcher).
 *
 * Pas de side effects au load — `openDb` n'est appelé que par les
 * modules `blobs.ts` / `aiMemory.ts` (à venir 2.4), lazy.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/** Nom de la database — versionné via `DB_VERSION`. */
export const DB_NAME = 'nestor';
/** Version courante. Bumper à chaque migration de schéma. */
export const DB_VERSION = 1;

/**
 * Une entrée du store `blobs` — sérialisée en `ArrayBuffer` plutôt
 * qu'en `Blob`.
 *
 * Pourquoi pas un `Blob` direct ? Les vrais navigateurs supportent
 * le structured clone d'un Blob dans IDB, mais l'écosystème de test
 * (JSDOM + fake-indexeddb) ne le sérialise pas proprement — au read
 * back on récupère un objet plain qui n'est plus une instance Blob.
 * Stocker l'`ArrayBuffer` est portable partout, et la conversion à
 * la lecture (`new Blob([buffer], { type })`) est gratuite.
 */
export interface BlobEntry {
  /** Clé applicative — convention `photo:<id>`, `voice:<msgId>`. */
  key: string;
  /** Bytes bruts du blob — sérialisable structured-clone-safe. */
  data: ArrayBuffer;
  /** MIME type (`image/jpeg`, `audio/webm`, etc.). */
  contentType: string;
  /** Taille en bytes (cache, évite de re-lire le blob). */
  size: number;
  /** Timestamp ms de l'ajout (cleanup futur si besoin). */
  addedAt: number;
}

/** Une entrée du store `ai-memory` (réservé incr. 2.4). */
export interface AiMemoryEntry {
  id: string;
  /** Sérialisation libre — typage strict ajouté en 2.4. */
  data: unknown;
  updatedAt: number;
}

/**
 * Une mutation en queue offline-first — Phase 3.3.1, étendue 3.3.5
 * pour les uploads binaires (photos, voicemails).
 *
 * Réplique d'une `apiRequest()` à rejouer plus tard quand le réseau
 * revient. Le shape matche directement `ApiRequestOptions` pour qu'à
 * la replay on puisse passer `{method, body}` tel quel à
 * `apiRequest(path, …)`.
 *
 * `seq` est attribué par IDB auto-increment — préserve l'ordre FIFO
 * naturel de la queue.
 *
 * `path` est une URL relative qui commence par `/` (cohérent avec
 * `apiRequest`). Le baseURL n'est pas stocké : si l'utilisateur
 * change d'env (dev → prod), les mutations queuées ciblent le
 * nouveau backend automatiquement.
 *
 * `attempts` track le nombre de tentatives de replay — utile pour
 * une stratégie de backoff (différée à 3.3.2+) et pour drop après
 * N échecs.
 *
 * **Phase 3.3.5** — `media` optionnel décrit un upload binaire R2.
 * Quand présent, le replay engine fait un flow 3-step (presign
 * upload-url → PUT R2 → confirm) au lieu d'un simple `apiRequest`.
 * `path` + `method` sont ignorés ; tout passe par les paths
 * `media.uploadUrlPath` et `media.confirmPath`.
 */
export interface MutationEntry {
  seq?: number;
  method: 'POST' | 'PATCH' | 'DELETE';
  path: string;
  /** Body JSON-sérialisé — `null` si pas de body (DELETE). */
  body: unknown;
  createdAt: number;
  attempts: number;
  /** Phase 3.3.5 — upload binaire R2 différé. */
  media?: MutationMedia;
  /**
   * Phase 3.3.7 — id local assigné par le store domain au moment
   * de l'enqueue (ex. `ev_local_1`, `tk_local_2`, UUID v4 client
   * pour les binaires). Au replay success, le serveur retourne un
   * id UUID server-side ; on émet alors un event `nestor:mutation
   * -remap {localId, serverId, path}` que les stores domain
   * écoutent pour remapper leur entrée locale → serveur. Évite le
   * doublon visuel transitoire au prochain GET refresh.
   *
   * Optionnel : si `undefined`, le mutateur ne porte pas d'id
   * local (cas des PATCH/DELETE où l'id de la cible est déjà
   * dans le path).
   */
  localId?: string;
}

/**
 * Métadonnées d'un upload binaire queue-able (Phase 3.3.5).
 *
 * Le blob lui-même vit dans le store `blobs` sous la clé `blobKey`.
 * Le replay engine récupère ce blob, demande un presigned URL
 * frais, PUT R2, puis confirme via le `confirmPath`.
 *
 * Deux flavors de confirm body selon `confirmKind` :
 *
 *   - `'voicemail'` (messages audio) : `audio: {messageId, mediaKey,
 *     mime, byteSize}` nested dans le body. `confirmExtras` peut
 *     contenir un `text?` optionnel.
 *   - `'photo'` (Phase 3.3.6+) : champs flats au top-level du body
 *     (`photoId, mediaKey, mime, byteSize` + `caption`, `albumName`,
 *     `takenAt` via `confirmExtras`).
 */
export interface MutationMedia {
  /** Clé IDB du blob source (ex. `voice:<localId>`, `photo:<localId>`). */
  blobKey: string;
  mime: string;
  byteSize: number;
  /** API path pour le presigned (ex. `/homes/<id>/messages/upload-url`). */
  uploadUrlPath: string;
  /** API path pour le confirm (ex. `/homes/<id>/messages`). */
  confirmPath: string;
  /** Flavor de confirm body. */
  confirmKind: 'voicemail' | 'photo';
  /** Champs additionnels au confirm body (ex. `{text}`, `{caption, albumName}`). */
  confirmExtras: Record<string, unknown>;
}

/** Schéma typé pour `idb` — `key` est le keyPath de chaque store. */
export interface NestorSchema extends DBSchema {
  blobs: {
    key: string;
    value: BlobEntry;
  };
  'ai-memory': {
    key: string;
    value: AiMemoryEntry;
  };
  mutations: {
    key: number;
    value: MutationEntry;
  };
}

let dbPromise: Promise<IDBPDatabase<NestorSchema>> | null = null;

/**
 * Ouvre (ou récupère depuis cache) la DB Nestor.
 *
 * Le callback `upgrade` crée les 3 stores au premier accès. Pour les
 * versions futures, on étend ce switch (`if (oldVersion < 2) ...`).
 *
 * Retourne `null` (résolu) si `indexedDB` n'est pas disponible (cas
 * Safari privacy mode très restreint, environnements de test sans
 * shim) — l'appelant doit gérer le fallback (Phase 2 = perte de
 * persistance pour les blobs ; le store homes.ts reste OK via
 * localStorage).
 */
export function openDb(): Promise<IDBPDatabase<NestorSchema> | null> {
  if (typeof indexedDB === 'undefined') {
    return Promise.resolve(null);
  }
  if (!dbPromise) {
    dbPromise = openDB<NestorSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Version 1 — création initiale des 3 stores.
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains('blobs')) {
            db.createObjectStore('blobs', { keyPath: 'key' });
          }
          if (!db.objectStoreNames.contains('ai-memory')) {
            db.createObjectStore('ai-memory', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('mutations')) {
            db.createObjectStore('mutations', {
              keyPath: 'seq',
              autoIncrement: true,
            });
          }
        }
      },
      blocked() {
        // Une autre tab est ouverte sur une version plus ancienne.
        // On laisse passer — la nouvelle version prendra le relais
        // à la prochaine ouverture.
        console.warn('[storage] open blocked — another tab holds an older version');
      },
      blocking() {
        // On bloque une autre tab qui veut ouvrir une nouvelle
        // version. On ferme proprement pour la laisser progresser.
        console.warn('[storage] blocking — closing this connection');
        void closeDb();
      },
    }).catch((err: unknown) => {
      console.warn('[storage] openDB failed:', err);
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise.catch(() => null);
}

/**
 * Ferme proprement la connexion DB et invalide le cache. Utile dans
 * les tests (avant `indexedDB.deleteDatabase`) ou si on veut forcer
 * une reconnexion (changement d'origine, par exemple).
 */
export async function closeDb(): Promise<void> {
  if (!dbPromise) return;
  try {
    const db = await dbPromise;
    db.close();
  } catch {
    // Connexion déjà fermée ou ouverture qui a fail — on ignore.
  }
  dbPromise = null;
}

/**
 * @internal pour tests — ferme la connexion (async) puis invalide
 * le cache. À appeler depuis `beforeEach` / `afterEach`.
 */
export async function _resetDbForTests(): Promise<void> {
  await closeDb();
}
